import { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

const STORAGE_KEY = 'invirtus_attribution';
const LINKED_KEY = 'invirtus_attribution_linked';

export interface AttributionData {
  visitor_id: string;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  fbclid: string | null;
  gclid: string | null;
  fbc: string | null;
  fbp: string | null;
  landing_page: string;
  referrer: string;
}

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

export function getAttribution(): AttributionData | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function getVisitorId(): string {
  const attr = getAttribution();
  if (attr?.visitor_id) return attr.visitor_id;
  return crypto.randomUUID();
}

export const useAttribution = () => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const apiUrl = import.meta.env.VITE_API_SERVER_URL;

  // First-touch: capture UTM params and send session start
  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY)) return;

    const params = new URLSearchParams(window.location.search);

    const data: AttributionData = {
      visitor_id: crypto.randomUUID(),
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      fbclid: params.get('fbclid'),
      gclid: params.get('gclid'),
      fbc: getCookie('_fbc'),
      fbp: getCookie('_fbp'),
      landing_page: window.location.pathname,
      referrer: document.referrer,
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));

    if (apiUrl) {
      fetch(`${apiUrl}/api/tracking/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }).catch(() => {});
    }
  }, [apiUrl]);

  // Link visitor_id to user_id once authenticated
  useEffect(() => {
    if (!isAuthenticated || !apiUrl) return;
    if (localStorage.getItem(LINKED_KEY)) return;

    const attr = getAttribution();
    if (!attr?.visitor_id) return;

    (async () => {
      try {
        const token = await getAccessTokenSilently();
        await fetch(`${apiUrl}/api/tracking/link`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ visitor_id: attr.visitor_id }),
        });
        localStorage.setItem(LINKED_KEY, 'true');
      } catch {
        // silent fail
      }
    })();
  }, [isAuthenticated, getAccessTokenSilently, apiUrl]);
};
